/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import React, { useState, useMemo } from 'react';
import type { InsightSynthesis } from '@g17eco/types/insights-synthesis';
import { InsightCard } from './partials/InsightCard';
import { InsightsMetrics } from './partials/InsightsMetrics';
import { InsightViewModal } from './partials/InsightViewModal';
import { InsightsPagination } from './partials/InsightsPagination';
import { SingleSelect } from '@g17eco/molecules/select/SingleSelect';

type SortOption = 'priorityScore' | 'confidence' | 'relevance' | 'clarity' | 'impact' | 'likelihood';

interface Props {
  insights: InsightSynthesis[];
}

const SORT_OPTIONS = [
  { label: 'Sort by Priority Score', value: 'priorityScore' },
  // { label: 'Sort by Confidence', value: 'confidence' },
  // { label: 'Sort by Relevance', value: 'relevance' },
  // { label: 'Sort by Clarity', value: 'clarity' },
  { label: 'Sort by Impact', value: 'impact' },
  { label: 'Sort by Likelihood', value: 'likelihood' },
];

const IMPACT_ORDER = { low: 1, medium: 2, high: 3, critical: 4 };
const LIKELIHOOD_ORDER = { low: 1, medium: 2, high: 3 };

export const IntelligenceView: React.FC<Props> = ({ insights }) => {
  const [selectedInsight, setSelectedInsight] = useState<InsightSynthesis | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<SortOption>('priorityScore');
  const itemsPerPage = 10;

  // Sorting logic
  const sortedInsights = useMemo(() => {
    return [...insights].sort((a, b) => {
      const aContent = a.content;
      const bContent = b.content;

      switch (sortBy) {
        case 'priorityScore':
          return bContent.priorityScore - aContent.priorityScore;
        case 'confidence':
          return bContent.confidence - aContent.confidence;
        case 'relevance':
          return bContent.relevance - aContent.relevance;
        case 'clarity':
          return bContent.clarity - aContent.clarity;
        case 'impact':
          return IMPACT_ORDER[bContent.impact] - IMPACT_ORDER[aContent.impact];
        case 'likelihood':
          const aLikelihood = aContent.likelihood || 'low';
          const bLikelihood = bContent.likelihood || 'low';
          return LIKELIHOOD_ORDER[bLikelihood] - LIKELIHOOD_ORDER[aLikelihood];
        default:
          return 0;
      }
    });
  }, [insights, sortBy]);

  // Pagination logic
  const { paginatedInsights, totalPages } = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return {
      paginatedInsights: sortedInsights.slice(startIndex, endIndex),
      totalPages: Math.ceil(sortedInsights.length / itemsPerPage),
    };
  }, [sortedInsights, currentPage, itemsPerPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleInsightClick = (insight: InsightSynthesis) => {
    setSelectedInsight(insight);
  };

  const handleCloseModal = () => {
    setSelectedInsight(null);
  };

  return (
    <>
      {/* Metrics Summary */}
      <InsightsMetrics insights={insights} />

      {/* Insights Grid */}
      {insights.length === 0 ? (
        <div className='text-center py-5'>
          <p className='text-ThemeTextMedium'>No insights match the selected filters.</p>
        </div>
      ) : (
        <>
          {/* Controls - Sort & Pagination */}
          <div className='d-flex justify-content-between align-items-center mb-3'>
            <div className='d-flex align-items-center gap-2'>
              <div style={{ minWidth: '180px', height: '30px' }}>
                <SingleSelect
                  isTransparent={true}
                  value={SORT_OPTIONS.find((opt) => opt.value === sortBy)}
                  onChange={(option) => {
                    if (option?.value) {
                      setSortBy(option.value as SortOption);
                      setCurrentPage(1); // Reset to first page when sorting changes
                    }
                  }}
                  options={SORT_OPTIONS}
                  isMenuPortalTargetBody={true}
                />
              </div>
            </div>
            <InsightsPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={sortedInsights.length}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
            />
          </div>

          <div className='d-flex flex-wrap gap-3'>
            {paginatedInsights.map((insight) => (
              <div key={insight._id} style={{ flex: '1 1 calc(50% - 12px)', minWidth: 0 }}>
                <InsightCard
                  insight={insight}
                  onDetailsClick={() => handleInsightClick(insight)}
                />
              </div>
            ))}
          </div>

          {/* Pagination Controls - Bottom */}
          <div className='border-top pt-2 mt-2'>
            <InsightsPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={sortedInsights.length}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
            />
          </div>
        </>
      )}

      {/* Unified Insight Modal */}
      {selectedInsight && (
        <InsightViewModal
          insight={selectedInsight}
          isOpen={true}
          onClose={handleCloseModal}
        />
      )}
    </>
  );
};
